import { useEffect, useRef } from "react";
import { Field } from "formik";
import WarningIcon from "@mui/icons-material/Warning";
import { pink } from "@mui/material/colors";
import {
  VIPSDataServiceURL,
  requestOptions,
} from "../../Constants/GlobalConstant";
import { getMessage } from "../../CommonFuntion/messages";
import { getVendorIdFromSession } from "../../CommonFuntion/TableBodyFormikHelper";
import {
  applyMaskOnBlur,
  MASK_BLUR_STRATEGY,
} from "../../CommonFuntion/applyMaskOnBlur";

const getCatalogueHeaderDataUrl = (
  element,
  catDropDown,
  tdkeys,
  user_id,
  store_grp
) => {
  let url = `${VIPSDataServiceURL}/Catalogue/GetCatalogueHeaderData?catId=${element?.catId}&pageID=${element?.pageId}`;
  Object.entries(catDropDown).forEach(([key, value]) => {
    url += `&key${parseInt(key)}=${value["key"][tdkeys]}`;
  });
  url += `&storeGrp=${store_grp}` + `&userId=${user_id}`;
  return url;
};

const getCatalogueKeyValuesUrl = (element, catDropDown, tdkeys) => {
  const vendorId = getVendorIdFromSession();
  let url = `${VIPSDataServiceURL}/Catalogue/GetCatalogueKeyValues?catId=${
    element?.catId
  }&KeyNo=${element?.keyIndex + 1}`;
  Object.entries(catDropDown).forEach(([key, value]) => {
    url += `&key${parseInt(key)}=${value["key"]?.[tdkeys] ?? ""}`;
  });
  url += `&vendorId=${encodeURIComponent(vendorId)}`;
  return url;
};

const updateFieldsWithData = (
  requiredData,
  schemaPageFields,
  tableName,
  tdkeys,
  setFieldValue
) => {
  requiredData.forEach((field) => {
    field.forEach((x) => {
      const catalougeFields = schemaPageFields.find(
        (y) => y.catFieldId === x.ID
      )?.fieldName;
      if (catalougeFields) {
        const catalougeFieldName = `${tableName}[${tdkeys}].${catalougeFields}`;
        setFieldValue(catalougeFieldName, x.Value);
      }
    });
  });
};

const clearFields = (
  schemaPageFields,
  tableName,
  tdkeys,
  setFieldValue,
  schemaType = 24
) => {
  const fields = schemaPageFields.filter(
    (y) => y.schemaType === schemaType && Number(y?.catalogueSchemaType) !== 1
  );
  fields.forEach((x) => {
    const name2 = `${tableName}[${tdkeys}].${x.fieldName}`;
    setFieldValue(name2, "");
  });
};

const hasNextCatalogueDropdown = (schemaPageFields, element) => {
  const currentKeyIndex = Number(element?.keyIndex);
  if (Number.isNaN(currentKeyIndex)) return false;

  const nextKeyIndex = currentKeyIndex + 1;
  const currentCatId = element?.catId;

  return (schemaPageFields || []).some((field) => {
    const isCatField = field?.prefix === "CAT";
    const isNextIndex = Number(field?.keyIndex) === nextKeyIndex;
    const isDropdown = Number(field?.catalogueSchemaType) === 3;
    const sameCatId =
      currentCatId === undefined ||
      field?.catId === undefined ||
      String(field?.catId) === String(currentCatId);

    return isCatField && isNextIndex && isDropdown && sameCatId;
  });
};

const resetKeys = (dropDownVal, startIndex, tdkeys, tableName, schemaPageFields, setFieldValue) => {
  // Reset keys and keyValues for all dropdowns from startIndex onwards
  Object.entries(dropDownVal).forEach(([k, v]) => {
    if (Number(k) >= startIndex) {
      v.key[tdkeys] = "";
      if (v.keyValues) v.keyValues[tdkeys] = [];
    }
  });

  // Reset form fields for catalogue fields with keyIndex > startIndex - 1 and not 0
  const fieldsToReset = schemaPageFields.filter(
    (field) => field.keyIndex !== 0 && field.keyIndex > startIndex - 1
  );

  fieldsToReset.forEach((field) => {
    const fieldName = `${tableName}[${tdkeys}].${field.fieldName}`;
    setFieldValue(fieldName, "");
  });
};
const CatalogTextboxField = ({
  element,
  name,
  tableName,
  tdkeys,
  setFieldValue,
  error,
  catDropDown,
  schemaPageFields,
  setCatTabData,
  setCatDropDown,
  user_id,
  store_grp,
}) => {
  const isCatTextbox =
    !!element && element.prefix === "CAT" && element.catalogueSchemaType === 1;
  const selectingRef = useRef(false);

  // Bind window callback for popup selection to this field (if present)
  useEffect(() => {
    if (!isCatTextbox) return;
    const handler = (nCatID, sKey1, sKey2, sKey3) => {
      try {
        selectingRef.current = true; // mark that we're updating from popup selection
        // Update dropdown state for up to three keys (0,1,2) for this row
        let dropDownVal = { ...catDropDown };
        const keysIn = [sKey1, sKey2, sKey3];
        const startIndex = Number(element?.keyIndex) || 0; // align sKey1 to this field's key index
        keysIn.forEach((val, offset) => {
          const idx = startIndex + offset;
          if (val === undefined) return;
          if (!dropDownVal[idx]) dropDownVal[idx] = {};
          if (!dropDownVal[idx].key) dropDownVal[idx].key = {};
          dropDownVal[idx].key[tdkeys] = val || "";
          if (dropDownVal[idx].keyValues) {
            dropDownVal[idx].keyValues[tdkeys] = val ? [{ label: val, value: val }] : [];
          }
        });
        setCatDropDown(dropDownVal);

        // Set current textbox value to sKey1
        setFieldValue(`${tableName}[${tdkeys}].${name}`, sKey1 || "");

        // Fetch header data and populate mapped fields
        const url = getCatalogueHeaderDataUrl(
          element,
          dropDownVal,
          tdkeys,
          user_id,
          store_grp
        );
        fetch(url, requestOptions)
          .then((r) => r.json())
          .then(({ catData }) => {
            if (catData?.length) {
              updateFieldsWithData(
                catData,
                schemaPageFields,
                tableName,
                tdkeys,
                setFieldValue
              );
            } else {
              clearFields(schemaPageFields, tableName, tdkeys, setFieldValue);
            }
          })
          .catch((err) => console.error("Error fetching header data:", err));

        // Update cat tab data key bundle
        let catalogueDetailDataKey = "";
        try {
          Object.entries(dropDownVal).forEach(([k, v]) => {
            const currentKey = v?.key?.[tdkeys] ?? "";
            catalogueDetailDataKey += `&key${parseInt(k, 10)}=${currentKey}`;
          });
        } catch (_) {}
        setCatTabData({ value: sKey1 || "", element, catalogueDetailDataKey });
      } catch (e) {
        console.error("onSelectCatKeyValues handler error:", e);
      } finally {
        // Defer clearing the flag to next tick to avoid onBlur race
        setTimeout(() => {
          selectingRef.current = false;
        }, 0);
      }
    };
    // Listen for global catalogue selection events (emitted by SelectCatRecord/onSelectCatKeyValues)
    const onCatalogueSelect = (e) => {
      const detail = e?.detail || {};
      handler(detail.catId, detail.sKey1, detail.sKey2, detail.sKey3);
    };
    window.addEventListener("catalogue:select", onCatalogueSelect);

    return () => {
      window.removeEventListener("catalogue:select", onCatalogueSelect);
    };
    // We want to rebind if these change, since they affect the handler
  }, [
    isCatTextbox,
    catDropDown,
    tdkeys,
    tableName,
    name,
    setFieldValue,
    setCatDropDown,
    setCatTabData,
    schemaPageFields,
    element,
    store_grp,
    user_id,
  ]);

  if (!isCatTextbox) return null;

  const textboxChange = async (e) => {
    // If a catalogue filter button click is in progress, ignore this blur
    try {
      if (typeof window !== "undefined" && window.mblnClickedFilterButton) {
        return;
      }
    } catch (_) {}
    if (selectingRef.current) {
      // Ignore blur triggered as part of popup selection to avoid clearing values
      return;
    }
    const rawValue = e?.target?.value ?? "";
    const formattedResult = applyMaskOnBlur(
      rawValue,
      element?.validation?.pictureMask,
      { strategy: MASK_BLUR_STRATEGY.CATALOGUE }
    );
    const keyIndex = element.keyIndex;
    const value = formattedResult.nextValue || "";
    if (!value || keyIndex === 0) return;

    // clone and set current key for this row
    let dropDownVal = { ...catDropDown };
    dropDownVal[keyIndex].key[tdkeys] = value;

    setCatDropDown(dropDownVal);

    if (element.lastKey) {
      // last key: fetch header data
      const url = getCatalogueHeaderDataUrl(
        element,
        dropDownVal,
        tdkeys,
        user_id,
        store_grp
      );
      try {
        const { catData } = await (await fetch(url, requestOptions)).json();
        if (catData.length) {
          updateFieldsWithData(
            catData,
            schemaPageFields,
            tableName,
            tdkeys,
            setFieldValue
          );
          setFieldValue(`${tableName}[${tdkeys}].${name}`, value);
        } else {
          alert(getMessage("noDataFound"));
          setFieldValue(`${tableName}[${tdkeys}].${name}`, "");
          clearFields(schemaPageFields, tableName, tdkeys, setFieldValue);
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
      // update tab data
      let catalogueDetailDataKey = "";
      Object.entries(dropDownVal).forEach(([k, v]) => {
        const currentKey = v?.key?.[tdkeys] ?? "";
        catalogueDetailDataKey += `&key${parseInt(k, 10)}=${currentKey}`;
      });
      setCatTabData({ value, element, catalogueDetailDataKey });
    } else {
      // intermediate key: fetch key values only if next field is Type 3 dropdown
      // Clear previously populated header/detail fields since downstream keys are changing
      clearFields(schemaPageFields, tableName, tdkeys, setFieldValue);
      setCatTabData({ value: "", element, catalogueDetailDataKey: "" });

      const shouldFetchNextDropdownValues = hasNextCatalogueDropdown(
        schemaPageFields,
        element
      );
      if (!shouldFetchNextDropdownValues) {
        // No downstream dropdown depends on this textbox key.
        setFieldValue(`${tableName}[${tdkeys}].${name}`, value);
        return;
      }

      // A downstream dropdown exists; reset subsequent keys for this row.
      resetKeys(
        dropDownVal,
        keyIndex + 1,
        tdkeys,
        tableName,
        schemaPageFields,
        setFieldValue
      );
      setCatDropDown(dropDownVal);

      const url = getCatalogueKeyValuesUrl(element, dropDownVal, tdkeys);
      try {
        const data = await (await fetch(url, requestOptions)).json();
        if (data.length) {
          resetKeys(dropDownVal, keyIndex + 1, tdkeys, tableName, schemaPageFields, setFieldValue);
          dropDownVal[keyIndex].keyValues[tdkeys] = data.map((x) => ({
            label: x,
            value: x,
          }));
          setCatDropDown(dropDownVal);
          setFieldValue(`${tableName}[${tdkeys}].${name}`, value);
        } else {
          alert(getMessage("noDataFound"));

          // Reset current key and all downstream keys
          dropDownVal[keyIndex].key[tdkeys] = "";
          if (dropDownVal[keyIndex].keyValues) dropDownVal[keyIndex].keyValues[tdkeys] = [];
          resetKeys(dropDownVal, keyIndex + 1, tdkeys, tableName, schemaPageFields, setFieldValue);
          clearFields(schemaPageFields, tableName, tdkeys, setFieldValue);
          setFieldValue(`${tableName}[${tdkeys}].${name}`, "");
          setCatTabData({ value: "", element, catalogueDetailDataKey: "" });
          setCatDropDown({ ...dropDownVal });
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    }
  };

  const maxLength = element.validation.pictureMask
    ? element.validation.pictureMask.replace(/"/g, "").length
    : "";

  const title =
    (element.validation.pictureMask
      ? "Picture Mask:" + element.validation.pictureMask.replace(/"/g, "")
      : "") + (element.validation.mandatory ? "\nMandatory" : "");

  return (
    <div className="wrapper">
      <div>
        <Field
          maxLength={maxLength}
          title={title}
          type="text"
          className="tableInput"
          id={`text-${tdkeys}`}
          name={`${tableName}[${tdkeys}].${name}`}
          data-cat-prefix="CAT"
          data-cat-key-index={element?.keyIndex}
          data-cat-tdkeys={tdkeys}
          onBlur={textboxChange}
          data-testid="CatalogTextboxField"
        />
        {error?.[name] && (
          <span className="icon">
            <WarningIcon sx={{ color: pink[500] }} />
          </span>
        )}
      </div>
    </div>
  );
};

export default CatalogTextboxField;
